import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Music, CheckCircle, Upload, Globe, Instagram, Youtube, ArrowLeft, X, Image, Disc, Loader2, Video, Calendar, MapPin, Clock, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Progress } from '@/components/ui/progress';

const API = "/api";

const GENRES = [
  "Hip Hop", "Rock", "Indie", "Jazz", "Blues", "R&B/Soul", "Pop", 
  "Electronic", "Folk", "Country", "Punk", "Metal", "Reggae", 
  "Latin", "World", "Classical", "Experimental", "Comedy", "Spoken Word", "Other"
];

const ARTIST_TYPES = [
  { value: "band", label: "Band/Group" },
  { value: "solo", label: "Solo Artist" },
  { value: "duo", label: "Duo" },
  { value: "instrumentalist", label: "Instrumentalist" },
  { value: "dj", label: "DJ/Producer" },
  { value: "comedian", label: "Comedian" },
  { value: "spoken_word", label: "Spoken Word/Poet" },
  { value: "other", label: "Other Performer" }
];

const RegisterBand = () => {
  const [submitted, setSubmitted] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedTracks, setUploadedTracks] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [albumArt, setAlbumArt] = useState(null);
  const [bannerImage, setBannerImage] = useState(null);
  
  const profileInputRef = useRef(null);
  const albumInputRef = useRef(null);
  const bannerInputRef = useRef(null);
  const trackInputRef = useRef(null);
  
  // Shorts (short-form video clips)
  const [shorts, setShorts] = useState([{ title: '', video_url: '', description: '' }]);
  
  // Upcoming Events/Shows
  const [events, setEvents] = useState([{ title: '', venue: '', date: '', time: '', description: '', ticket_url: '', price_tier: 'free' }]);
  
  const [form, setForm] = useState({
    band_name: '',
    artist_type: 'band',
    contact_name: '',
    email: '',
    phone: '',
    genre: '',
    bio: '',
    hometown: 'Minneapolis',
    years_active: '',
    members: '',
    website: '',
    spotify_url: '',
    soundcloud_url: '',
    bandcamp_url: '',
    youtube_channel: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    music_links: [''],
    video_links: [''],
    press_photos: [''],
    booking_contact: '',
    press_kit_url: '',
    achievements: '',
    profile_image_url: '',
    album_art_url: '',
    banner_image_url: ''
  });

  // Image upload handler
  const handleImageUpload = async (file, imageType, setImageState) => {
    if (!file) return;
    
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image too large. Maximum size is 10MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', imageType);
    
    try {
      setUploadProgress(prev => ({ ...prev, [imageType]: 0 }));
      
      const response = await axios.post(`${API}/upload/band-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [imageType]: progress }));
        }
      });
      
      setImageState({ url: response.data.url, file: file });
      setForm(prev => ({ ...prev, [`${imageType}_image_url`]: response.data.url }));
      toast.success(`${imageType.charAt(0).toUpperCase() + imageType.slice(1)} image uploaded!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadProgress(prev => ({ ...prev, [imageType]: null }));
    }
  };

  // Track upload handler
  const handleTrackUpload = async (file) => {
    if (!file) return;
    
    if (uploadedTracks.length >= 6) {
      toast.error('Maximum 6 tracks allowed');
      return;
    }
    
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      toast.error('Track too large. Maximum size is 50MB');
      return;
    }
    
    // Prompt for track title
    const title = window.prompt('Enter track title:', file.name.replace(/\.[^/.]+$/, ''));
    if (!title) return;
    
    // Need registration ID first - create temp ID if not submitted yet
    let regId = registrationId;
    if (!regId) {
      regId = `temp-${Date.now()}`;
      setRegistrationId(regId);
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('registration_id', regId);
    
    const trackId = `uploading-${Date.now()}`;
    
    try {
      setUploadProgress(prev => ({ ...prev, [trackId]: 0 }));
      setUploadedTracks(prev => [...prev, { id: trackId, title, uploading: true }]);
      
      const response = await axios.post(`${API}/upload/band-track`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [trackId]: progress }));
        }
      });
      
      setUploadedTracks(prev => prev.map(t => 
        t.id === trackId ? { ...response.data, uploading: false } : t
      ));
      toast.success(`"${title}" uploaded successfully!`);
    } catch (error) {
      setUploadedTracks(prev => prev.filter(t => t.id !== trackId));
      toast.error(error.response?.data?.detail || 'Failed to upload track');
    } finally {
      setUploadProgress(prev => ({ ...prev, [trackId]: null }));
    }
  };

  // Delete track handler
  const handleDeleteTrack = async (trackId) => {
    try {
      await axios.delete(`${API}/upload/band-track/${trackId}`);
      setUploadedTracks(prev => prev.filter(t => t.id !== trackId));
      toast.success('Track removed');
    } catch (error) {
      toast.error('Failed to remove track');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Client-side validation
    if (!form.band_name.trim()) {
      toast.error('Band name is required');
      setLoading(false);
      return;
    }
    if (!form.contact_name.trim()) {
      toast.error('Contact name is required');
      setLoading(false);
      return;
    }
    if (!form.email.trim()) {
      toast.error('Email is required');
      setLoading(false);
      return;
    }
    if (!form.email.includes('@')) {
      toast.error('Please enter a valid email address');
      setLoading(false);
      return;
    }
    if (!form.genre.trim()) {
      toast.error('Genre is required');
      setLoading(false);
      return;
    }
    if (!form.bio.trim()) {
      toast.error('Bio is required - tell us about your music!');
      setLoading(false);
      return;
    }
    if (!form.hometown.trim()) {
      toast.error('Hometown is required');
      setLoading(false);
      return;
    }
    
    try {
      // Clean up arrays - remove empty strings
      const cleanForm = {
        ...form,
        years_active: form.years_active ? parseInt(form.years_active) : null,
        music_links: form.music_links.filter(l => l.trim()),
        video_links: form.video_links.filter(l => l.trim()),
        press_photos: form.press_photos.filter(l => l.trim())
      };
      
      // Clean up shorts - only include ones with video URL
      const cleanShorts = shorts.filter(s => s.video_url.trim()).map(s => ({
        title: s.title.trim() || 'Untitled Short',
        video_url: s.video_url.trim(),
        description: s.description.trim() || ''
      }));
      
      // Clean up events - only include ones with required fields
      const cleanEvents = events.filter(e => e.title.trim() && e.venue.trim() && e.date).map(e => ({
        title: e.title.trim(),
        venue: e.venue.trim(),
        date: e.date,
        time: e.time || '19:00',
        description: e.description.trim() || '',
        ticket_url: e.ticket_url.trim() || '',
        price_tier: e.price_tier || 'free'
      }));
      
      const response = await axios.post(`${API}/register/band`, {
        ...cleanForm,
        submitted_shorts: cleanShorts,
        submitted_events: cleanEvents,
        uploaded_track_ids: uploadedTracks.map(t => t.id)
      });
      setRegistrationId(response.data.id);
      setSubmitted(true);
      toast.success('Registration submitted successfully!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to submit registration. Please check all fields and try again.';
      toast.error(errorMsg);
      console.error('Registration error:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field, index, value) => {
    setForm(prev => {
      const arr = [...prev[field]];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addArrayField = (field) => {
    setForm(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  // Shorts management
  const updateShort = (index, field, value) => {
    setShorts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addShort = () => {
    if (shorts.length < 5) {
      setShorts(prev => [...prev, { title: '', video_url: '', description: '' }]);
    }
  };

  const removeShort = (index) => {
    if (shorts.length > 1) {
      setShorts(prev => prev.filter((_, i) => i !== index));
    } else {
      setShorts([{ title: '', video_url: '', description: '' }]);
    }
  };

  // Events management
  const updateEvent = (index, field, value) => {
    setEvents(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addEvent = () => {
    if (events.length < 5) {
      setEvents(prev => [...prev, { title: '', venue: '', date: '', time: '', description: '', ticket_url: '', price_tier: 'free' }]);
    }
  };

  const removeEvent = (index) => {
    if (events.length > 1) {
      setEvents(prev => prev.filter((_, i) => i !== index));
    } else {
      setEvents([{ title: '', venue: '', date: '', time: '', description: '', ticket_url: '', price_tier: 'free' }]);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="band-registration-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md space-y-6"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-secondary/20 flex items-center justify-center">
            <CheckCircle size={40} className="text-secondary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Registration Submitted!</h1>
          <p className="text-muted-foreground">
            Thank you for registering with Twin Tube! We'll review your submission and get back to you within 3-5 business days.
          </p>
          <div className="p-4 bg-card rounded-sm border border-border">
            <p className="text-sm text-muted-foreground mb-1">Your Reference ID:</p>
            <code className="text-primary font-mono text-sm">{registrationId}</code>
          </div>
          <p className="text-sm text-muted-foreground">
            Once approved, you'll appear in our artist directory and your content will be available for streaming!
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/">
              <Button variant="outline">Back to Home</Button>
            </Link>
            <Link to="/submit">
              <Button>Submit an Event</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="register-band-page">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft size={16} />
        Back to Home
      </Link>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
          <Music size={32} className="text-primary" />
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Artist & Performer Registry
        </h1>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Join the Twin Cities entertainment community on Twin Tube. Musicians, bands, comedians, 
          and performers - get discovered, promote your shows, and connect with local fans.
        </p>
      </motion.div>

      {/* Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Basic Information</CardTitle>
              <CardDescription>Tell us about yourself or your act</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Artist/Act Name *</Label>
                  <Input
                    value={form.band_name}
                    onChange={(e) => updateForm('band_name', e.target.value)}
                    placeholder="Your name or act name"
                    required
                    data-testid="band-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Artist Type *</Label>
                  <Select value={form.artist_type} onValueChange={(v) => updateForm('artist_type', v)}>
                    <SelectTrigger data-testid="artist-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ARTIST_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Genre *</Label>
                  <Select value={form.genre} onValueChange={(v) => updateForm('genre', v)} required>
                    <SelectTrigger data-testid="genre-select">
                      <SelectValue placeholder="Select genre" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENRES.map(g => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Bio *</Label>
                <Textarea
                  value={form.bio}
                  onChange={(e) => updateForm('bio', e.target.value)}
                  placeholder="Tell us about yourself or your act's story, sound, and what makes you unique..."
                  rows={4}
                  required
                  data-testid="bio-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Hometown *</Label>
                  <Select value={form.hometown} onValueChange={(v) => updateForm('hometown', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Minneapolis">Minneapolis</SelectItem>
                      <SelectItem value="St. Paul">St. Paul</SelectItem>
                      <SelectItem value="Twin Cities">Twin Cities Area</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Years Active</Label>
                  <Input
                    type="number"
                    value={form.years_active}
                    onChange={(e) => updateForm('years_active', e.target.value)}
                    placeholder="e.g., 5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Members/Collaborators</Label>
                  <Input
                    value={form.members}
                    onChange={(e) => updateForm('members', e.target.value)}
                    placeholder="e.g., John (vocals), Jane (guitar) - leave blank for solo"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">Contact Information</CardTitle>
              <CardDescription>How can we reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Name *</Label>
                  <Input
                    value={form.contact_name}
                    onChange={(e) => updateForm('contact_name', e.target.value)}
                    placeholder="Primary contact person"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateForm('email', e.target.value)}
                    placeholder="band@email.com"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => updateForm('phone', e.target.value)}
                    placeholder="(612) 555-0123"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Booking Contact</Label>
                  <Input
                    value={form.booking_contact}
                    onChange={(e) => updateForm('booking_contact', e.target.value)}
                    placeholder="booking@email.com"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Social & Streaming Links */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Globe size={20} /> Online Presence
              </CardTitle>
              <CardDescription>Your website and social media links</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://yourband.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Spotify</Label>
                  <Input
                    value={form.spotify_url}
                    onChange={(e) => updateForm('spotify_url', e.target.value)}
                    placeholder="https://open.spotify.com/artist/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>SoundCloud</Label>
                  <Input
                    value={form.soundcloud_url}
                    onChange={(e) => updateForm('soundcloud_url', e.target.value)}
                    placeholder="https://soundcloud.com/yourband"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Bandcamp</Label>
                  <Input
                    value={form.bandcamp_url}
                    onChange={(e) => updateForm('bandcamp_url', e.target.value)}
                    placeholder="https://yourband.bandcamp.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>YouTube Channel</Label>
                  <Input
                    value={form.youtube_channel}
                    onChange={(e) => updateForm('youtube_channel', e.target.value)}
                    placeholder="https://youtube.com/@yourband"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Instagram</Label>
                  <Input
                    value={form.instagram}
                    onChange={(e) => updateForm('instagram', e.target.value)}
                    placeholder="@yourband"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Facebook</Label>
                  <Input
                    value={form.facebook}
                    onChange={(e) => updateForm('facebook', e.target.value)}
                    placeholder="https://facebook.com/yourband"
                  />
                </div>
                <div className="space-y-2">
                  <Label>TikTok</Label>
                  <Input
                    value={form.tiktok}
                    onChange={(e) => updateForm('tiktok', e.target.value)}
                    placeholder="@yourband"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Images & Album Art */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Image size={20} /> Profile Images & Artwork
              </CardTitle>
              <CardDescription>Upload your band photo, album artwork, and banner image (Max 10MB each)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Image */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  Band/Artist Photo
                </Label>
                <div className="flex items-center gap-4">
                  {profileImage?.url ? (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted">
                      <img src={profileImage.url} alt="Profile" className="w-full h-full object-cover" />
                      <button 
                        type="button"
                        onClick={() => { setProfileImage(null); setForm(prev => ({ ...prev, profile_image_url: '' })); }}
                        className="absolute top-1 right-1 p-1 bg-destructive rounded-full"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => profileInputRef.current?.click()}
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {uploadProgress.profile !== null && uploadProgress.profile !== undefined ? (
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" />
                          <span className="text-xs">{uploadProgress.profile}%</span>
                        </div>
                      ) : (
                        <Upload size={24} className="text-muted-foreground" />
                      )}
                    </div>
                  )}
                  <input 
                    ref={profileInputRef}
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'profile', setProfileImage)}
                  />
                  <div className="text-sm text-muted-foreground">
                    <p>Main profile photo</p>
                    <p className="text-xs">JPG, PNG, WebP (Square recommended)</p>
                  </div>
                </div>
              </div>

              {/* Album Art */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Disc size={16} /> Album/EP Cover Art
                </Label>
                <div className="flex items-center gap-4">
                  {albumArt?.url ? (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden bg-muted">
                      <img src={albumArt.url} alt="Album" className="w-full h-full object-cover" />
                      <button 
                        type="button"
                        onClick={() => { setAlbumArt(null); setForm(prev => ({ ...prev, album_art_url: '' })); }}
                        className="absolute top-1 right-1 p-1 bg-destructive rounded-full"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => albumInputRef.current?.click()}
                      className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {uploadProgress.album !== null && uploadProgress.album !== undefined ? (
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" />
                          <span className="text-xs">{uploadProgress.album}%</span>
                        </div>
                      ) : (
                        <Disc size={24} className="text-muted-foreground" />
                      )}
                    </div>
                  )}
                  <input 
                    ref={albumInputRef}
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'album', setAlbumArt)}
                  />
                  <div className="text-sm text-muted-foreground">
                    <p>Latest album or EP artwork</p>
                    <p className="text-xs">Square format recommended</p>
                  </div>
                </div>
              </div>

              {/* Banner Image */}
              <div className="space-y-3">
                <Label>Profile Banner</Label>
                <div className="space-y-2">
                  {bannerImage?.url ? (
                    <div className="relative w-full h-32 rounded-lg overflow-hidden bg-muted">
                      <img src={bannerImage.url} alt="Banner" className="w-full h-full object-cover" />
                      <button 
                        type="button"
                        onClick={() => { setBannerImage(null); setForm(prev => ({ ...prev, banner_image_url: '' })); }}
                        className="absolute top-2 right-2 p-1 bg-destructive rounded-full"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => bannerInputRef.current?.click()}
                      className="w-full h-32 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
                    >
                      {uploadProgress.banner !== null && uploadProgress.banner !== undefined ? (
                        <div className="text-center">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-1" />
                          <span className="text-xs">{uploadProgress.banner}%</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <Upload size={24} className="text-muted-foreground mx-auto mb-1" />
                          <span className="text-xs text-muted-foreground">Upload Banner (1200x400 recommended)</span>
                        </div>
                      )}
                    </div>
                  )}
                  <input 
                    ref={bannerInputRef}
                    type="file" 
                    accept="image/*" 
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], 'banner', setBannerImage)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upload Your Music */}
          <Card className="bg-card border-border border-primary/30">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Music size={20} className="text-primary" /> Upload Your Tracks
              </CardTitle>
              <CardDescription>
                Upload up to 6 tracks directly (MP3 or WAV only). 
                These will be available for streaming on Twin Tube once approved.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Track List */}
              {uploadedTracks.length > 0 && (
                <div className="space-y-2">
                  {uploadedTracks.map((track) => (
                    <div key={track.id} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <Music size={16} className="text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{track.title}</p>
                        {track.uploading ? (
                          <div className="mt-1">
                            <Progress value={uploadProgress[track.id] || 0} className="h-1" />
                          </div>
                        ) : track.duration && (
                          <p className="text-xs text-muted-foreground">
                            {Math.floor(track.duration / 60)}:{String(Math.floor(track.duration % 60)).padStart(2, '0')}
                          </p>
                        )}
                      </div>
                      {!track.uploading && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteTrack(track.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <X size={14} />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              {uploadedTracks.length < 6 && (
                <div>
                  <input 
                    ref={trackInputRef}
                    type="file" 
                    accept=".mp3,.wav,audio/mpeg,audio/wav" 
                    className="hidden"
                    onChange={(e) => handleTrackUpload(e.target.files[0])}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => trackInputRef.current?.click()}
                    className="w-full border-dashed"
                  >
                    <Upload size={16} className="mr-2" />
                    Upload Track ({uploadedTracks.length}/6)
                  </Button>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                Or link to existing tracks below if you prefer
              </p>
            </CardContent>
          </Card>

          {/* Music & Video Submissions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Upload size={20} /> External Music & Video Links
              </CardTitle>
              <CardDescription>Link to your music and videos on other platforms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Music Links */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Music size={16} /> Music Embed URLs (YouTube or SoundCloud)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Paste YouTube or SoundCloud URLs for your tracks. These will be playable on Twin Tube.
                </p>
                {form.music_links.map((link, i) => (
                  <Input
                    key={i}
                    value={link}
                    onChange={(e) => updateArrayField('music_links', i, e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=... or https://soundcloud.com/..."
                  />
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayField('music_links')}>
                  + Add Another Track
                </Button>
              </div>

              {/* Video Links */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Youtube size={16} /> Video Links (for Shorts section)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Paste YouTube video URLs to be featured in our Shorts section.
                </p>
                {form.video_links.map((link, i) => (
                  <Input
                    key={i}
                    value={link}
                    onChange={(e) => updateArrayField('video_links', i, e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayField('video_links')}>
                  + Add Another Video
                </Button>
              </div>

              {/* Press Photos */}
              <div className="space-y-3">
                <Label>Press Photo URLs</Label>
                <p className="text-xs text-muted-foreground">
                  Paste direct links to high-quality press photos (first one will be your main image).
                </p>
                {form.press_photos.map((link, i) => (
                  <Input
                    key={i}
                    value={link}
                    onChange={(e) => updateArrayField('press_photos', i, e.target.value)}
                    placeholder="https://... (direct image URL)"
                  />
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => addArrayField('press_photos')}>
                  + Add Another Photo
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Shorts / Short-form Video Content */}
          <Card className="bg-card border-border border-secondary/30">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Video size={20} className="text-secondary" /> Submit Shorts Content
              </CardTitle>
              <CardDescription>
                Share short video clips (TikTok-style) for our Shorts section. Perfect for music previews, behind-the-scenes, or promo clips.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {shorts.map((short, i) => (
                <div key={i} className="p-4 border border-border rounded-lg space-y-3 relative">
                  {shorts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeShort(i)}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                    >
                      <X size={14} />
                    </Button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Title</Label>
                      <Input
                        value={short.title}
                        onChange={(e) => updateShort(i, 'title', e.target.value)}
                        placeholder="Short video title"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Video URL (YouTube, TikTok, Instagram)</Label>
                      <Input
                        value={short.video_url}
                        onChange={(e) => updateShort(i, 'video_url', e.target.value)}
                        placeholder="https://www.youtube.com/shorts/... or TikTok link"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description (optional)</Label>
                    <Input
                      value={short.description}
                      onChange={(e) => updateShort(i, 'description', e.target.value)}
                      placeholder="Brief description of the video"
                    />
                  </div>
                </div>
              ))}
              {shorts.length < 5 && (
                <Button type="button" variant="outline" size="sm" onClick={addShort} className="w-full border-dashed">
                  <Video size={14} className="mr-2" /> Add Another Short ({shorts.length}/5)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events/Shows */}
          <Card className="bg-card border-border border-green-500/30">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calendar size={20} className="text-green-500" /> Upcoming Shows & Events
              </CardTitle>
              <CardDescription>
                Have shows coming up? Add them here to get listed on the Twin Tube events calendar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {events.map((event, i) => (
                <div key={i} className="p-4 border border-border rounded-lg space-y-3 relative">
                  {events.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEvent(i)}
                      className="absolute top-2 right-2 text-muted-foreground hover:text-destructive"
                    >
                      <X size={14} />
                    </Button>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1"><Music size={12} /> Event Title</Label>
                      <Input
                        value={event.title}
                        onChange={(e) => updateEvent(i, 'title', e.target.value)}
                        placeholder="e.g., Live at First Avenue"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1"><MapPin size={12} /> Venue</Label>
                      <Input
                        value={event.venue}
                        onChange={(e) => updateEvent(i, 'venue', e.target.value)}
                        placeholder="e.g., First Avenue, Minneapolis"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1"><Calendar size={12} /> Date</Label>
                      <Input
                        type="date"
                        value={event.date}
                        onChange={(e) => updateEvent(i, 'date', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1"><Clock size={12} /> Time</Label>
                      <Input
                        type="time"
                        value={event.time}
                        onChange={(e) => updateEvent(i, 'time', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs flex items-center gap-1"><DollarSign size={12} /> Price</Label>
                      <Select value={event.price_tier} onValueChange={(v) => updateEvent(i, 'price_tier', v)}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="$">$ (Under $20)</SelectItem>
                          <SelectItem value="$$">$$ ($20-50)</SelectItem>
                          <SelectItem value="$$$">$$$ ($50+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Ticket Link</Label>
                      <Input
                        value={event.ticket_url}
                        onChange={(e) => updateEvent(i, 'ticket_url', e.target.value)}
                        placeholder="https://tickets..."
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description (optional)</Label>
                    <Textarea
                      value={event.description}
                      onChange={(e) => updateEvent(i, 'description', e.target.value)}
                      placeholder="Tell fans what to expect at the show..."
                      rows={2}
                    />
                  </div>
                </div>
              ))}
              {events.length < 5 && (
                <Button type="button" variant="outline" size="sm" onClick={addEvent} className="w-full border-dashed">
                  <Calendar size={14} className="mr-2" /> Add Another Show ({events.length}/5)
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Accordion type="single" collapsible>
            <AccordionItem value="additional" className="border border-border rounded-sm bg-card">
              <AccordionTrigger className="px-4">Additional Information (Optional)</AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-4">
                <div className="space-y-2">
                  <Label>Press Kit URL</Label>
                  <Input
                    value={form.press_kit_url}
                    onChange={(e) => updateForm('press_kit_url', e.target.value)}
                    placeholder="https://... (EPK or press kit link)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Achievements & Notable Performances</Label>
                  <Textarea
                    value={form.achievements}
                    onChange={(e) => updateForm('achievements', e.target.value)}
                    placeholder="Awards, festivals played, notable venues, press coverage, etc."
                    rows={3}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Submit */}
          <Button 
            type="submit" 
            size="lg" 
            className="w-full gap-2"
            disabled={loading}
            data-testid="submit-btn"
          >
            {loading ? 'Submitting...' : 'Submit Registration'}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            By submitting, you confirm that you have the rights to the music and content provided.
            We typically review submissions within 3-5 business days.
          </p>
        </form>
      </motion.div>
    </div>
  );
};

export default RegisterBand;
